import z from 'zod'
import { nullSafeBoolean, nullSafeNumber } from '../shared/schema'

// All Soundcheck calibration lives on one document, settings/soundcheck, so a
// live operator can turn the notifications on and tune every confirmation
// period in one place without a deploy. Field names are unprefixed because the
// document already namespaces them.
//
// Health checks and telemetry run whether or not notifications are enabled; the
// only thing the switch changes is whether the dialog and banner appear. There
// is no blocking switch: AV validation and speech-driven participation depend on
// the same measurement, so turning it off never stops MicCheck.
export const schema = z.object({
  // Show the dialog and banner. Health checks and telemetry run when false.
  notificationsEnabled: nullSafeBoolean(false),
  // Account for elapsed time and evaluate pending checks on one controller clock.
  tickMS: nullSafeNumber(5000),
  // Allow microphone startup or restart to settle before confirming an unusable
  // track.
  micGraceMS: nullSafeNumber(5000),
  // Allow the meeting microphone to publish after the student joins before
  // confirming no_track. 15s: the publish lands 5-11s after av_check_passed,
  // so the 5s micGraceMS raised a false no_track for 42 of the 58 users who
  // logged one on 2026-09-24. Matches the legacy meeting_audio_track_failed
  // grace.
  publishGraceMS: nullSafeNumber(15000),
  // Separate the two digital-zero reads that confirm digital_silence.
  silenceConfirmDelayMS: nullSafeNumber(5000),
  // Re-read an active track so a mic that dies mid-session is noticed.
  silencePollIntervalMS: nullSafeNumber(15000),
  // Loudness (RMS, 0-1 full scale) a live mic must reach at least once per
  // nearSilenceMS. 0.001 (-60 dBFS): in a 10-minute sample of the worker's
  // per-minute peaks on 2026-09-24, 455 of 464 publishing mics reached 0.02
  // and the quietest working mics idled around 0.002, while mics nobody could
  // hear sat at 0.0001.
  nearSilenceRMS: nullSafeNumber(0.001),
  // How long a meeting mic may stay under nearSilenceRMS before near_silence.
  nearSilenceMS: nullSafeNumber(60000),
  // The same, in the AV check, where a student waits seconds, not minutes.
  avCheckNearSilenceMS: nullSafeNumber(15000),
  // Allow server speech confirmation to arrive before a delivery failure.
  reachabilityDelayMS: nullSafeNumber(10000),
  // Retain recent local and server speech evidence for the delivery check.
  reachabilityWindowMS: nullSafeNumber(60000),
  // Require meaningful local speech before a delivery failure.
  reachabilityMinSpeechMS: nullSafeNumber(8000),
  // Qualify a speaking period for the transcript-flow wait.
  transcriptMinSpeechMS: nullSafeNumber(10000),
  // Allow transcript progress to arrive after a qualifying pause.
  noTranscriptAfterMS: nullSafeNumber(120000),
  // Detect unmuted solo discussion without any transcript flow.
  unmutedNoTranscriptAfterMS: nullSafeNumber(180000),
  // Report missing or inactive Secretary status.
  heartbeatTimeoutMS: nullSafeNumber(30000),
  // Confirm no_transcript once the Secretary's status has left this mic out
  // for this long while the student spoke. 30s: after a same-identity takeover
  // in room DGgDFohGknnWqUMqmfKw the worker never picked up the new track, and
  // waiting out noTranscriptAfterMS took 4 minutes to say so.
  notListeningConfirmMS: nullSafeNumber(30000),
  // Confirm no_transcript when the server heard this much of the student's
  // speech inside reachabilityWindowMS and not one caption arrived. Captions,
  // interim ones included, follow speech within seconds. 15s: in room
  // ToYb6zKoy8ndZZesnj1s the server heard ~20s over two minutes, the worker
  // detected no speech in it, and the student reloaded before the pause-based
  // wait ran out.
  serverSpeechNoCaptionMS: nullSafeNumber(15000),
})

export const empty = Object.freeze(schema.parse({}))

export type FirestoreSettingsSoundcheck = z.infer<typeof schema>
